import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash, { Flash } from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatBoolean from "go-core/components/Formatters/FormatBoolean";
import FormatDate, { defaultFormatDate } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi } from "../../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../../utils/entityStatus/entityStatus";
import { useHasInvoicePermission } from "../../../../hooks/useHasInvoicePermission";
import { invoiceListName, invoiceResourceType } from "../../../../utils";
import { exportEInvoice } from "../../services/exportInvoice";

interface ListState {
	resource?: Record<string, any>;
}

const buildConfig = (
	data: Record<string, any>,
	t: TFunction,
	location: Location,
	organization: OrganizationState,
	segmentContext: SegmentListService,
	handleChangeStatus: (invoice: InvoiceApi, toStatus: "close" | "remove" | "cancel") => Promise<void>,
	addFlash: (_: Flash) => void,
	hasInvoiceManagePermission: boolean,
	hasInvoiceClosePermission: boolean,
	hasPermissionsToCreateNewInvoice: boolean
) => {
	let config = {
		fields: [
			{
				id: "issueDate",
				name: t("modules.invoice.field.issued.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return (
						<StickyColumnWithEntityStatus
							status={convertListStatusToEntityStatus(item.status, [
								{ status: "CLOSED", label: "ACTIVE" },
							])}
							to={`${location.pathname}/${item.id}`}
						>
							{FormatDate(item.dates?.issue_date, undefined, false, defaultFormatDate)}
						</StickyColumnWithEntityStatus>
					);
				},
				renderExport: (item: InvoiceApi) =>
					FormatDate(item.dates?.issue_date, undefined, false, defaultFormatDate),
			},
			{
				id: "document_number",
				name: t("common.word.document"),
				render: (item: InvoiceApi) => {
					return (
						<Link to={`${location.pathname}/${item.id}`}>
							{item.status === "DRAFT"
								? `${t("enums.invoices.document_type.PROFORMA")} #${item.id}`
								: `${t("enums.invoices.document_type.INVOICE")} #${item.document_number.full_number}`}
						</Link>
					);
				},
				renderExport: (item: InvoiceApi) => {
					return `${t(`enums.invoices.document_type.INVOICE`)} ${item.document_number.full_number}`;
				},
			},
			{
				id: "recipient",
				name: t("modules.invoice.field.recipient.title"),
				render: (item: InvoiceApi) => {
					if (item.recipient && item.recipient_id) {
						return (
							<div className="d-flex flex-column">
								<RenderLimitedText minWidth={200}>
									<Link
										rel={"noreferrer"}
										target={"_blank"}
										to={`/${organization.id}/contacts/${item.recipient_id}`}
									>
										{item.recipient.name}
									</Link>
								</RenderLimitedText>
							</div>
						);
					}
				},
				renderExport: (item: InvoiceApi) => item.recipient?.name,
			},
			{
				id: "tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				render: (item: InvoiceApi) => item.recipient?.tax_id_no,
			},
			{
				id: "orders",
				name: t("modules.invoice.field.orders.title"),
				render: (item: InvoiceApi) => {
					return (
						<div className="d-flex flex-column">
							{item.orders
								?.filter((order) => order.order_id)
								.map((order) => (
									<Link
										key={`order_${order.order_id}`}
										rel={"noreferrer"}
										target={"_blank"}
										to={`/${organization.id}/documents/sales/${order.order_id}`}
									>
										{order.order_number}
									</Link>
								))}
						</div>
					);
				},
				renderExport: (item: InvoiceApi) => item.orders?.map((order) => order?.order_number).join(", "),
				disableSorting: true,
			},
			{
				id: "price_sum_net",
				name: t("modules.invoice.field.net_sum.title"),
				render: (item: InvoiceApi) => FormatMoney(item.summary?.price_sum_net),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "price_sum_gross",
				name: t("modules.invoice.field.gross_sum.title"),
				render: (item: InvoiceApi) => FormatMoney(item.summary?.price_sum_gross),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "saleDate",
				name: t("modules.invoice.field.sale_date.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return FormatDate(item.dates?.sale_date, undefined, false, defaultFormatDate);
				},
			},
			{
				id: "payDate",
				name: t("modules.invoice.field.pay_date.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return FormatDate(item.dates?.pay_date, undefined, false, defaultFormatDate);
				},
			},
			{
				id: "statuses",
				name: t("lib:common.word.status"),
				render: (item: InvoiceApi) => (
					<AppFormatResourceStatusLabel
						status={item.status}
						customStatuses={[{ status: "CLOSED", label: "success" }]}
					/>
				),
				renderExport: (item: InvoiceApi) => t(`enums.common.status.${item.status}`),
				disableSorting: true,
			},
			{
				id: "payment_methods",
				name: t("modules.invoice.field.payment_method.title"),
				disableSorting: true,
				render: (item: InvoiceApi) => {
					const uniquePayments = Array.from(new Set(item.payments.map((payment) => payment.payment_type)));
					return uniquePayments.map((payment) => t(`enums.invoice.payment_type.${payment}`)).join(", ");
				},
			},
			{
				id: "paid",
				name: t("modules.invoice.field.paid.title"),
				type: "boolean",
				render: (item: InvoiceApi) => <FormatBoolean value={item.paid} />,
				renderExport: (item: InvoiceApi) => {
					return `${item.paid ? t("lib:common.word.yes") : t("lib:common.word.no")}`;
				},
				disableSorting: true,
			},
		],
		filters: [
			{
				id: "contacts",
				name: t("modules.invoice.field.recipient.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getContactSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "orderIds",
				name: t("modules.invoice.field.orders.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getOrderSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "daysPastPayDate",
				name: t("modules.invoice.field.days_past_pay_data.title"),
				type: "number",
			},
			{
				id: "statuses",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					CLOSED: t("enums.common.status.CLOSED"),
					DRAFT: t("enums.common.status.DRAFT"),
				},
				hasDefaultOptions: false,
			},
		],
		actions: [
			{
				name: t("lib:common.action.preview"),
				link: (item: InvoiceApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("lib:common.action.edit"),
				link: (item: InvoiceApi) => `${location.pathname}/${item.id}/edit`,
				visible: (item: InvoiceApi) =>
					item.status === "DRAFT" ? hasPermissionsToCreateNewInvoice : hasInvoiceManagePermission,
			},
			{
				name: t("lib:common.action.confirm"),
				click: (item: InvoiceApi) => handleChangeStatus(item, "close"),
				visible: (item: InvoiceApi) =>
					item.status === "DRAFT" && (hasInvoiceClosePermission || hasInvoiceManagePermission),
				refreshListAfter: true,
			},
			{
				name: t("lib:common.action.cancel"),
				click: (item: InvoiceApi) => handleChangeStatus(item, "cancel"),
				visible: (item: InvoiceApi) => item.status === "CLOSED" && hasInvoiceManagePermission,
				refreshListAfter: true,
			},
			{
				name: t("lib:common.action.remove"),
				click: (item: InvoiceApi) => handleChangeStatus(item, "remove"),
				refreshListAfter: true,
				visible: () => hasInvoiceManagePermission,
			},
			{
				name: t("modules.invoice.field.e-invoice.title"),
				click: (item: InvoiceApi) => exportEInvoice(item, t, addFlash),
			},
		],
		customPageSizes: ["20", "100", "1000"],
		selectedColumns: [
			"issueDate",
			"document_number",
			"recipient",
			"tax_id_no",
			"orders",
			"price_sum_net",
			"price_sum_gross",
			"saleDate",
			"payDate",
			"statuses",
			"payment_methods",
			"paid",
		],
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.invoice.field.export_config.title"),
			filename: t("modules.invoice.header.title"),
			organization: organization.name,
		},
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "summary,recipient,orders,custom_fields,payments";
			return apiOrganization.getInvoices(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(invoiceListName, invoiceResourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 2,
		fetchTotalItemsCountForSelectedFilters: (params: Record<string, any>, sourceToken?: CancelTokenSource) =>
			apiOrganization.getInvoicesCount(params, { cancelToken: sourceToken?.token }),
	} as ListConfig;

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return config;
};

const List = ({ resource }: ListState) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const isMobile = useWindowSize().isMobile;
	const { hasInvoiceManagePermission, hasInvoiceClosePermission, hasPermissionsToCreateNewInvoice } =
		useHasInvoicePermission();

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.add"),
			path: `/${organization.id}/documents/invoices/new`,
			variant: "primary",
			hide: !hasPermissionsToCreateNewInvoice,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.add"),
			path: `/${organization.id}/documents/invoices/new`,
			hide: !hasPermissionsToCreateNewInvoice,
		},
	];
	const location = useLocation();
	const segmentContextValue = useContext(SegmentContext);
	const history = useHistory();
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();

	const handleChangeStatus = async (invoice: InvoiceApi, toStatus: "close" | "remove" | "cancel") => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message:
					toStatus === "remove" ? t("lib:confirmation.message.remove") : t("lib:confirmation.message.action"),
			});
			const params = { include: "all" };
			switch (toStatus) {
				case "close":
					await apiOrganization.closeInvoice(invoice.id, params);
					break;
				case "cancel":
					await apiOrganization.cancelInvoice(invoice.id, params);
					break;
				case "remove":
					await apiOrganization.removeInvoice(invoice.id);
					break;
			}
			if (toStatus === "remove") {
				addSuccessFlash(t("lib:common.flash.removed"));
				history.push(`/${organization.id}/documents/invoices`);
			} else {
				addSuccessFlash(t("lib:common.flash.completed"));
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(
		data,
		t,
		location,
		organization,
		segmentContextValue,
		handleChangeStatus,
		addFlash,
		hasInvoiceManagePermission,
		hasInvoiceClosePermission,
		hasPermissionsToCreateNewInvoice
	);

	return (
		<>
			{!isMobile && <Header title={t("modules.invoice.header.title")} buttons={buttons} />}
			<ListData
				config={config}
				emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
				mobileActions={mobileActions}
				canManageMultiActionsForAllItems
				shouldChangeSegmentFromOutside
			/>
		</>
	);
};

const OrganizationDocumentInvoiceIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const customFieldResourceTypes = [
		{
			type: invoiceResourceType,
			name: t(`enums.custom_fields.resources.INVOICE`),
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.invoice.header.title"));
		setResource(
			wrapPromise(segmentContextValue.get(invoiceListName, invoiceResourceType, customFieldResourceTypes))
		);
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} />
		</Suspense>
	);
};

export default OrganizationDocumentInvoiceIndexPage;
